import {FormControlProps, TextField, TextFieldProps} from '@mui/material';
import {nanoid} from 'nanoid/non-secure';
import {ChangeEvent, FocusEvent, type JSX, useEffect} from 'react';
import {Controller, FieldPath, FieldValues, useFormContext, useFormState} from 'react-hook-form';
import {PropsWithoutChildren} from '../react/types';

export type AllowedTextFieldProps = Omit<TextFieldProps, 'children' | 'error'>;

type AllowedFormControlProps = Omit<FormControlProps<typeof TextField>, 'error'>;
export interface BaseControlledTextInputFieldAdapterProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends PropsWithoutChildren<AllowedFormControlProps> {
  name: TName;
}

interface ControlledTextInputFieldAdapterProps<
  TFieldType,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends BaseControlledTextInputFieldAdapterProps<TFieldValues, TName> {
  defaultValue: unknown; // Default value must be specified to avoid switching from uncontrolled to controlled
  valueTransformer?: (value: string) => TFieldType;
}

// Do not use this component directly
// Instead, use one of the components that imports this (e.g. TextInputField, IntegerInputField, FloatInputField)
export function ControlledTextInputFieldAdapter<TFieldType>({
  autoFocus,
  defaultValue,
  helperText,
  id = nanoid(), // id required to associate label with input element
  name,
  onBlur,
  onChange,
  valueTransformer,
  ...muiProps
}: ControlledTextInputFieldAdapterProps<TFieldType>): JSX.Element {
  const {control, setFocus} = useFormContext();
  const {errors} = useFormState();
  const error = errors[name]?.message as string;

  useEffect(() => {
    if (autoFocus) {
      setFocus(name);
    }
  }, [autoFocus, name, setFocus]);

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({field: {ref, ...field}}) => {
        return (
          <TextField
            {...muiProps}
            {...field}
            error={!!error}
            helperText={error ?? helperText}
            id={id}
            inputRef={ref}
            onBlur={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              field.onBlur();
              if (onBlur) {
                onBlur(event);
              }
            }}
            onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              const value = event.target.value;
              const update = valueTransformer ? valueTransformer(value) : value;
              field.onChange(update);
              if (onChange) {
                onChange(event);
              }
            }}
          />
        );
      }}
    />
  );
}
